/**
 * Erkennt Vertipper im lokalen Teil (vor dem @), z. B. fumk_christian → funk_christian.
 * Kein SMTP-Beweis — stoppt nur, wenn genau ein Wörterbuch-Treffer mit Edit-Distanz 1 existiert.
 */

const FIRST_NAMES = `
alexander andreas anja anna antonia axel barbara benjamin bernd birgit brigitte
carina carolin caroline christian christiane christoph claudia cornelia
daniel daniela david dennis diana dirk dominik doris
elena elias elisabeth elke emma eric erik eva evelyn
fabian felix florian frank franziska friedrich
gabriele gerhard gregor
hanna hannah hans heike heinrich helmut henriette holger
ines inge irene isabel
jan jana jasmin jennifer jessica joachim johann johanna johannes jonas julia julian juergen jürgen
karin karl karla katharina katrin kerstin kevin kim klaas klaus klemens konrad kristina
lara lars laura lea lena leon lisa lothar louise luca lucas ludwig luka lukas lydia
magdalena marco maria marianne mario marion markus marta martin martina matthias max maximilian melanie michael michaela miriam monika moritz
nadine nico nicole niklas nina norbert
oliver olivia oscar oskar otto
patricia patrick paul petra philipp
ralf rebecca regina rene reneé richard robert robin roger roland rudi rudolf ruth
sabine sandra sarah sascha sebastian silke simon simone sonja stefan stefanie steffen stephanie susanne susi sven
tanja thomas thorsten tim tobias torsten
ulrike ursula udo ulli
vanessa vera verena victoria viktor
walter werner wilhelm wolfgang
yasmin yvonne
`.trim().split(/\s+/);

const SURNAMES = `
albrecht arnold
bauer baumann becker berg bergmann bock brandt braun
dietrich
engel
fischer frank friedrich fuchs funk
graf gross groß
hartmann heinrich herrmann hofmann hoffmann huber
jung
kaiser keller klein koehler kohler krüger kruger kuhn
lang lehmann
maier mayer meier meyer moeller moller müller mueller
neumann
otto
peters
richter
schafer schaefer schäfer schmidt schmitt schneider scholz schreiber schroeder schröder schubert schulz schulze schumacher schwarz
seidel simon stein
thomas
vogel
wagner weber weiss weiß werner wolf wolff
zimmermann
`.trim().split(/\s+/);

const MAIL_TOKENS = `
info kontakt office team mail webmaster service rechnung buchhaltung termin
salon friseur hair beauty kosmetik barber studio praxis
chef inhaber inhaberin empfang anmeldung
christian funk klaus tanja ramin
`.trim().split(/\s+/);

const DICT = new Set(
  [...FIRST_NAMES, ...SURNAMES, ...MAIL_TOKENS]
    .map((w) => w.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss'))
    .filter((w) => w.length >= 4)
);

const LETTERS = 'abcdefghijklmnopqrstuvwxyz';

function edits1(word: string): Set<string> {
  const results = new Set<string>();
  const n = word.length;
  for (let i = 0; i < n; i += 1) {
    results.add(word.slice(0, i) + word.slice(i + 1));
  }
  for (let i = 0; i < n - 1; i += 1) {
    results.add(word.slice(0, i) + word[i + 1] + word[i] + word.slice(i + 2));
  }
  for (let i = 0; i < n; i += 1) {
    for (const c of LETTERS) {
      if (c !== word[i]) results.add(word.slice(0, i) + c + word.slice(i + 1));
    }
  }
  for (let i = 0; i <= n; i += 1) {
    for (const c of LETTERS) {
      results.add(word.slice(0, i) + c + word.slice(i));
    }
  }
  return results;
}

function tokenizeLocalPart(localPart: string): string[] {
  return localPart
    .toLowerCase()
    .split(/[._+-]+/)
    .map((t) => t.replace(/\d+/g, ''))
    .filter((t) => t.length >= 4);
}

/**
 * Echte Mail-Locals sind oft Name + Initial/Jahr-Suffix: elkeB80 → „elkeb“,
 * christianf → „christianf“. Das ist KEIN Tippfehler für „elke“ / „christian“.
 */
function looksLikeNameWithShortAffix(token: string): boolean {
  for (let len = token.length - 1; len >= 4; len -= 1) {
    const prefix = token.slice(0, len);
    const suffix = token.slice(len);
    if (DICT.has(prefix) && suffix.length >= 1 && suffix.length <= 2) return true;
  }
  for (let start = 1; start <= 2; start += 1) {
    const prefix = token.slice(0, start);
    const rest = token.slice(start);
    if (rest.length >= 4 && DICT.has(rest) && prefix.length >= 1) return true;
  }
  return false;
}

/** Nur echte Buchstaben-Vertauscher/Ersetzungen — nicht „Buchstabe löschen → Vorname“. */
function isLikelySpellingFix(from: string, to: string): boolean {
  if (from.length === to.length) return true; // Substitution oder Transposition
  // Insertion/Deletion nur, wenn kein Name+Affix-Muster vorliegt (bereits vorher gefiltert)
  // und die Längendifferenz genau 1 ist — aber nie, wenn `to` nur ein Vorname und `from` = Vorname+1–2 Zeichen
  if (Math.abs(from.length - to.length) !== 1) return false;
  const longer = from.length > to.length ? from : to;
  const shorter = from.length > to.length ? to : from;
  if (DICT.has(shorter) && longer.startsWith(shorter) && longer.length - shorter.length <= 2) {
    return false;
  }
  if (DICT.has(shorter) && longer.endsWith(shorter) && longer.length - shorter.length <= 2) {
    return false;
  }
  return true;
}

export function findLocalPartTypo(email: string): { suggestion: string; reason: string } | null {
  const at = email.indexOf('@');
  if (at < 1) return null;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const tokens = tokenizeLocalPart(local);
  if (tokens.length === 0) return null;

  const replacements: Array<{ from: string; to: string }> = [];

  for (const token of tokens) {
    if (DICT.has(token)) continue;
    // elkeB / elkeb1980 / christianf → normal, nicht blockieren
    if (looksLikeNameWithShortAffix(token)) continue;

    const candidates = [...edits1(token)]
      .filter((w) => DICT.has(w) && w !== token)
      .filter((w) => isLikelySpellingFix(token, w));
    const unique = [...new Set(candidates)];
    if (unique.length === 1) {
      replacements.push({ from: token, to: unique[0] });
    } else if (unique.length > 1 && unique.length <= 3) {
      return {
        suggestion: unique.map((u) => local.replace(token, u) + '@' + domain).join(', '),
        reason: `Tippfehler im Namen „${token}“ — meintest du ${unique.join(' / ')}? So kommt die Mail sehr wahrscheinlich nicht an.`,
      };
    }
  }

  if (replacements.length === 0) return null;

  let suggestedLocal = local;
  for (const { from, to } of replacements) {
    suggestedLocal = suggestedLocal.replace(from, to);
  }
  if (suggestedLocal === local) return null;

  return {
    suggestion: `${suggestedLocal}@${domain}`,
    reason: `Tippfehler erkannt — meintest du ${suggestedLocal}@${domain}? „${replacements.map((r) => r.from).join(', ')}“ ist so sehr wahrscheinlich unzustellbar.`,
  };
}
